<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateProject',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    preventDefaultOnClose: {
      type: Boolean,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const formName = ref('project');
  const formId = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateProject');
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = async () => {
    return new $fn.value.Resource(item.value);
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };
  const $$load_container = async () => {
    return {
      self: $fn.value.urlFor('create_project'),
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'resource', dependsOn: ['item'] },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      resource,
      editor,
      container,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('Project', 'Project');
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Project', 'New project') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.preventDefaultOnClose,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventDefaultOnClose');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ax1hh6ycb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_setData_b4fkn7oct_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: editor.value.getData(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_closeComponent_6wsmom2of_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_navigate_4ci1qmre0_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.project',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const inst_evh_1040167445267876_created_suw1li5vc_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_reloadSlickgrid_rtrdf28k0_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'projects',
    };
  };
  const inst_evh_1040167445267876_created_b1i4rjvz5_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_rest_mu7ea0g4h_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_ax1hh6ycb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ax1hh6ycb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_x1n0qa7zj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ax1hh6ycb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_x1n0qa7zj],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_n5vslg645 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_iuct760f7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_n5vslg645,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_iuct760f7],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_soz2dpxid =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_67f2dyei6 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_soz2dpxid,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_67f2dyei6],
    event: 'close',
  });
  const act_1040167445267876_setData_b4fkn7oct = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_b4fkn7oct_getActionArgs,
  });
  const inst_evh_1040167445267876_before_gnqikgnhs =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_b4fkn7oct,
      event: 'before',
      displayName: 'setData',
    });
  const act_1040167445267876_closeComponent_6wsmom2of =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_6wsmom2of_getActionArgs,
    });
  const inst_evh_1040167445267876_created_vjku7ppo7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_6wsmom2of,
      event: 'created',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_4ci1qmre0 =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_4ci1qmre0_getActionArgs,
    });
  const inst_evh_1040167445267876_created_suw1li5vc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_4ci1qmre0,
      event: 'created',
      when: inst_evh_1040167445267876_created_suw1li5vc_getWhen,
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_rtrdf28k0 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_rtrdf28k0_getActionArgs,
    });
  const inst_evh_1040167445267876_created_b1i4rjvz5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_rtrdf28k0,
      event: 'created',
      when: inst_evh_1040167445267876_created_b1i4rjvz5_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_rest_mu7ea0g4h = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_mu7ea0g4h_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_before_gnqikgnhs,
      inst_evh_1040167445267876_created_vjku7ppo7,
      inst_evh_1040167445267876_created_suw1li5vc,
      inst_evh_1040167445267876_created_b1i4rjvz5,
    ],
  });
  const inst_evh_1040167445267876_submit_95dkdvg1t =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_mu7ea0g4h,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_95dkdvg1t],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_d6o4wq9fh =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_r6ku9vs8m =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_d6o4wq9fh,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_r6ku9vs8m],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_x1n0qa7zj,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_iuct760f7,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_67f2dyei6,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_before_gnqikgnhs,
    inst_evh_1040167445267876_created_vjku7ppo7,
    inst_evh_1040167445267876_created_suw1li5vc,
    inst_evh_1040167445267876_created_b1i4rjvz5,
    inst_evh_1040167445267876_submit_95dkdvg1t,
    create: inst_evh_1040167445267876_submit_95dkdvg1t,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_r6ku9vs8m,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_x1n0qa7zj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_x1n0qa7zj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_iuct760f7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_iuct760f7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_67f2dyei6 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_67f2dyei6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_before_gnqikgnhs = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_before_gnqikgnhs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_vjku7ppo7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_vjku7ppo7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_suw1li5vc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_suw1li5vc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_b1i4rjvz5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_b1i4rjvz5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_95dkdvg1t = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_95dkdvg1t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_r6ku9vs8m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_r6ku9vs8m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateProject"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateProject"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetProjectGeneral :project="editor"> </FieldsetProjectGeneral>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Create')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
